import { defineStore } from "pinia";
import api from "../api";
import type { RouteRegistration } from "../types/routeRegistrations";

export type ActiveRegistrationState = "none" | "soon" | "overdue";

const SOON_THRESHOLD_MINUTES = 60;
const REFRESH_INTERVAL_MS = 60_000;

// Ключ "скрыто" привязан к (id, срок): после продления срок другой -> ключ
// другой -> баннер снова показывается.
export const bannerDismissKey = (r: Pick<RouteRegistration, "id" | "expected_return_at">) =>
  `eco_reg_banner_hidden:${r.id}:${r.expected_return_at}`;

const readDismissed = (r: RouteRegistration): string | null => {
  const key = bannerDismissKey(r);
  try {
    return sessionStorage.getItem(key) ? key : null;
  } catch {
    return null;
  }
};

// Растёт при каждой мутации и каждом refresh: ответ refresh, начатого до
// мутации (или устаревший относительно более нового refresh), отбрасывается.
let seq = 0;

let timer: ReturnType<typeof setInterval> | null = null;
let onVisible: (() => void) | null = null;

export const useActiveRegistrationStore = defineStore("activeRegistration", {
  state: () => ({
    registration: null as RouteRegistration | null,
    now: Date.now(),
    dismissedKey: null as string | null,
  }),
  getters: {
    minutesLeft(state): number | null {
      if (!state.registration) return null;
      const diff = new Date(state.registration.expected_return_at).getTime() - state.now;
      return Math.ceil(diff / 60_000);
    },
    state(): ActiveRegistrationState {
      if (!this.registration) return "none";
      const diff = new Date(this.registration.expected_return_at).getTime() - this.now;
      if (diff <= 0) return "overdue";
      if (diff <= SOON_THRESHOLD_MINUTES * 60_000) return "soon";
      return "none";
    },
    // overdue скрыть нельзя; soon - только если скрыт именно для текущего срока.
    bannerVisible(): boolean {
      if (this.state === "none" || !this.registration) return false;
      if (this.state === "overdue") return true;
      return this.dismissedKey !== bannerDismissKey(this.registration);
    },
  },
  actions: {
    async refresh() {
      const mine = ++seq;
      try {
        const res = await api.get<RouteRegistration>("/route-registrations/active");
        if (mine !== seq) return;
        this.registration = res.data;
        this.dismissedKey = readDismissed(res.data);
      } catch (e) {
        if (mine !== seq) return;
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404) this.registration = null;
        // Прочие ошибки (сеть, 5xx): оставляем прежнее состояние.
      }
      this.now = Date.now();
    },
    async extend(minutes = 60) {
      if (!this.registration) await this.refresh();
      if (!this.registration) return;
      const res = await api.post<RouteRegistration>(
        `/route-registrations/${this.registration.id}/extend`,
        { minutes },
      );
      seq++;
      this.registration = res.data;
      this.now = Date.now();
      this.dismissedKey = readDismissed(res.data);
    },
    async markReturned() {
      if (!this.registration) await this.refresh();
      if (!this.registration) return;
      await api.post(`/route-registrations/${this.registration.id}/return`);
      seq++;
      this.registration = null;
    },
    hideBanner() {
      if (this.state !== "soon" || !this.registration) return;
      const key = bannerDismissKey(this.registration);
      this.dismissedKey = key;
      try {
        sessionStorage.setItem(key, "1");
      } catch {
        // storage недоступен - скрытие живёт только в памяти.
      }
    },
    start() {
      if (timer) return;
      void this.refresh();
      timer = setInterval(() => {
        this.now = Date.now();
        void this.refresh();
      }, REFRESH_INTERVAL_MS);
      onVisible = () => {
        if (document.visibilityState === "visible") void this.refresh();
      };
      document.addEventListener("visibilitychange", onVisible);
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = null;
      if (onVisible) document.removeEventListener("visibilitychange", onVisible);
      onVisible = null;
      this.registration = null;
      this.dismissedKey = null;
    },
  },
});
